import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';

/**
 * Reusable, in-context regulatory disclaimer.
 *
 * Placed alongside signals, AI overviews, alerts, and backtest/hypothetical
 * output to make clear the Service provides general, impersonal information
 * only — not personalized investment advice or a recommendation.
 *
 * Variants:
 *  - "banner"  (default): full-width notice for the top of a page/section
 *  - "compact": condensed single-line notice
 *  - "hypothetical": specialized wording for backtest / simulated results
 */
const DEFAULT_TEXT = (
  <>
    <strong>Informational purposes only — not investment advice.</strong>{' '}
    Signals, AI overviews, and other content are general information, are not
    personalized to your circumstances, and are not a recommendation,
    solicitation, or endorsement to buy or sell any security. Do your own
    research and consult a licensed financial, legal, or tax professional
    before making any investment decision.
  </>
);

const HYPOTHETICAL_TEXT = (
  <>
    <strong>Hypothetical / backtested results.</strong> These signals are
    simulated using historical data, do not reflect actual trading, and exclude
    fees, commissions, slippage, and taxes.{' '}
    <strong>Past or backtested performance does not guarantee future results.</strong>{' '}
    For informational purposes only — not investment advice or a recommendation.
  </>
);

const Disclaimer = ({ variant = 'banner', children, showTermsLink = true, className = '' }) => {
  const body =
    children != null
      ? children
      : variant === 'hypothetical'
        ? HYPOTHETICAL_TEXT
        : DEFAULT_TEXT;

  return (
    <div
      className={`bidsig-disclaimer bidsig-disclaimer--${variant} ${className}`.trim()}
      role="note"
      aria-label="Disclaimer"
    >
      <Info size={15} className="bidsig-disclaimer-icon" aria-hidden />
      <p className="bidsig-disclaimer-text">
        {body}
        {showTermsLink && variant !== 'compact' && (
          <>
            {' '}
            <Link to="/terms" className="bidsig-disclaimer-link">
              See full Terms &amp; Disclaimer
            </Link>
            .
          </>
        )}
      </p>
    </div>
  );
};

export default Disclaimer;
